import React, { useEffect, useState } from 'react';
import { Card, Typography, Button, Space, Spin, message } from 'antd';
import { ArrowLeftOutlined, ShareAltOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import QRCode from 'qrcode';

const { Title, Text, Paragraph } = Typography;

const InvitePage: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [inviteUrl, setInviteUrl] = useState<string>('');

  // 检查用户是否已登录
  useEffect(() => {
    if (!authState.isLoading && !authState.isAuthenticated) {
      navigate('/login');
    }
  }, [authState.isLoading, authState.isAuthenticated, navigate]);

  // 生成邀请链接和二维码
  useEffect(() => {
    if (authState.user?.username) {
      // 构造邀请链接
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/login?referer_name=${authState.user.username}`;
      setInviteUrl(url);

      // 生成二维码
      QRCode.toDataURL(url, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })
        .then((dataUrl) => {
          setQrCodeUrl(dataUrl);
        })
        .catch((err) => {
          console.error('生成二维码失败:', err);
          message.error('生成二维码失败');
        });
    }
  }, [authState.user]);

  // 复制邀请链接
  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteUrl)
      .then(() => {
        message.success('邀请链接已复制到剪贴板');
      })
      .catch((err) => {
        console.error('复制失败:', err);
        message.error('复制失败，请手动复制');
      });
  };

  // 如果正在加载，显示loading状态
  if (authState.isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // 如果未登录，返回null
  if (!authState.isAuthenticated) {
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* 顶部导航 */}
      <div style={{
        width: '100%',
        maxWidth: '800px',
        marginBottom: '40px'
      }}>
        <Button 
          type="text" 
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          style={{ color: 'white', fontSize: '16px' }}
        >
          返回首页
        </Button>
      </div>

      {/* 主内容卡片 */}
      <Card
        style={{
          width: '100%',
          maxWidth: '600px',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}
        bodyStyle={{
          padding: window.innerWidth < 768 ? '32px 16px' : '48px 32px',
          textAlign: 'center'
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={2} style={{ marginBottom: '8px' }}>
              邀请新用户注册
            </Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              分享二维码或链接，邀请好友加入达康AI医生
            </Text>
          </div>

          {/* 二维码显示 */}
          {qrCodeUrl ? (
            <div style={{
              padding: '24px',
              backgroundColor: '#f5f5f5',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '350px',
              margin: '0 auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <img 
                src={qrCodeUrl} 
                alt="邀请二维码" 
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  height: 'auto',
                  display: 'block'
                }}
              />
            </div>
          ) : (
            <Spin tip="生成二维码中..." />
          )}

          {/* 邀请说明 */}
          <div style={{
            backgroundColor: '#e6f7ff',
            padding: '16px',
            borderRadius: '8px',
            textAlign: 'left'
          }}>
            <Paragraph style={{ margin: 0 }}>
              <ShareAltOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
              <strong>分享方式：</strong>
            </Paragraph>
            <Paragraph style={{ marginBottom: 0, paddingLeft: '24px' }}>
              • 截图保存二维码分享给好友<br />
              • 好友扫码即可注册并绑定推荐关系<br />
              • 或直接复制下方邀请链接发送给好友
            </Paragraph>
          </div>

          {/* 邀请链接 */}
          <div style={{
            width: '100%',
            padding: '16px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            wordBreak: 'break-all',
            textAlign: 'left'
          }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              邀请链接：
            </Text>
            <div>
              <Text copyable={{ text: inviteUrl }}>
                {inviteUrl}
              </Text>
            </div>
          </div>

          {/* 操作按钮 */}
          <Space size="large">
            <Button 
              type="primary" 
              icon={<CopyOutlined />}
              size="large"
              onClick={handleCopyLink}
            >
              复制邀请链接
            </Button>
            <Button 
              size="large"
              onClick={() => navigate('/')}
            >
              返回首页
            </Button>
          </Space>
        </Space>
      </Card>

      {/* 底部信息 */}
      <div style={{ marginTop: '40px', textAlign: 'center' }}>
        <Text style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
          © 2025 达康AI医生. 专注于预问诊的智能健康平台
        </Text>
      </div>
    </div>
  );
};

export default InvitePage;


